/* eslint-disable react-hooks/set-state-in-effect, react-hooks/refs --
 * v77.99.77: Month-view drag-to-resize state machine. Uses mutable
 * refs read during render to keep the live cursor cell + event in
 * sync, and seeds local state inside an effect that registers the
 * window pointer listeners. The experimental hooks rules are too
 * coarse to express the contract here.
 */
import { useEffect, useRef, useState } from "react";

/**
 * Encapsulates the Month-View drag-to-resize gesture:
 *   - long-press an event chip to start
 *   - drag across day cells to extend the end
 *   - drop on a day cell to commit (calls openEditRef.current with the new end)
 *   - Escape / pointer-cancel aborts cleanly.
 *
 * `openEditRef` and `toLocalInputRef` are React refs so the hook can be
 * called BEFORE the consumer declares those callbacks (CalendarPage
 * declares its `openEdit` ~290 lines below the hook call — that const
 * would be in the temporal dead zone if we read it eagerly here).
 * The consumer assigns `openEditRef.current = openEdit` after declaring it.
 */
export function useMonthResize({ isMediator, openEditRef, toLocalInputRef }) {
  const [monthResize, setMonthResize] = useState(null);
  const monthResizeStateRef = useRef({ justFinished: false, current: null });
  const longPressMonthRef = useRef(null);

  // Clear any pending long-press timer when the component unmounts so
  // we don't fire setMonthResize on a torn-down tree.
  useEffect(() => {
    return () => {
      if (longPressMonthRef.current) clearTimeout(longPressMonthRef.current);
    };
  }, []);

  /** Find the day-cell key under the given clientX/Y, or null. */
  const findCellKey = (clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    if (!el) return null;
    const cell = el.closest('[data-testid^="day-"]');
    if (!cell) return null;
    // testid shape: `day-YYYY-M-D` — slice off the prefix.
    const tid = cell.getAttribute("data-testid") || "";
    const key = tid.startsWith("day-") ? tid.slice(4) : null;
    // Ignore non day-of-month testids (handover/override sub-elements
    // also start with "day-" prefix). The cell ones are exactly
    // YYYY-M-D so they look like `day-2026-5-20`.
    if (!key || !/^\d{4}-\d{1,2}-\d{1,2}$/.test(key)) return null;
    return key;
  };

  const updateMonthResize = (e) => {
    const r = monthResizeStateRef.current.current;
    if (!r) return;
    const key = findCellKey(e.clientX, e.clientY);
    if (!key || key === r.cursorDayKey) return;
    // Parse YYYY-M-D → Date with the original end's time-of-day preserved.
    const [yy, mm, dd] = key.split("-").map(Number);
    const candidate = new Date(yy, mm, dd, r.origEnd.getHours(), r.origEnd.getMinutes(), 0, 0);
    // Clamp: never below origStart + 15 min (mirrors Week View min duration).
    const minEnd = new Date(r.origStart.getTime() + 15 * 60 * 1000);
    const newEndDate = candidate < minEnd ? minEnd : candidate;
    r.cursorDayKey = key;
    r.newEndDate = newEndDate;
    setMonthResize({ ...r });
  };

  const finishMonthResize = () => {
    const r = monthResizeStateRef.current.current;
    if (!r) return;
    const { ev, origEnd, newEndDate } = r;
    monthResizeStateRef.current.current = null;
    setMonthResize(null);
    monthResizeStateRef.current.justFinished = true;
    // No-op if user didn't drag onto a different day, or dropped on a
    // day that resolves to the same end.
    if (!newEndDate) return;
    if (newEndDate.getTime() === origEnd.getTime()) return;
    const oe = openEditRef.current;
    const tli = toLocalInputRef.current;
    if (oe && tli) oe(ev, { end: tli(newEndDate.toISOString()) });
  };

  const cancelMonthResize = () => {
    if (longPressMonthRef.current) {
      clearTimeout(longPressMonthRef.current);
      longPressMonthRef.current = null;
    }
    monthResizeStateRef.current.current = null;
    setMonthResize(null);
  };

  const beginMonthResize = (ev, pointerId) => {
    if (isMediator) return;
    const start = new Date(ev.start);
    const end = ev.end ? new Date(ev.end) : new Date(start.getTime() + 60 * 60 * 1000);
    const r = {
      eventId: ev.id,
      ev,
      origStart: start,
      origEnd: end,
      cursorDayKey: null,
      newEndDate: null,
      pointerId,
    };
    monthResizeStateRef.current.current = r;
    setMonthResize(r);

    const onMove = (e) => {
      if (!monthResizeStateRef.current.current) return;
      e.preventDefault();
      updateMonthResize(e);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      finishMonthResize();
    };
    const onCancel = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
      cancelMonthResize();
    };
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
  };

  // Escape mid-drag = cancel (matches Week View).
  useEffect(() => {
    if (!monthResize) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") cancelMonthResize();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [monthResize]);

  return {
    monthResize,
    monthResizeStateRef,
    longPressMonthRef,
    beginMonthResize,
    cancelMonthResize,
    updateMonthResize,
  };
}
